import * as _ from "lodash";
import cn from "classnames";
import React, { useCallback, useMemo } from "react";
import { useToggle } from "react-use";

import { useAuth0AccessTokenContext } from "../../common/Auth0AccessToken";
import LoginButton from "../../common/LoginButton";
import ModalPopup from "../../common/ModalPopup";
import { useProfileContext, ProfileContextAuthenticatedInterface } from "../../remote";
import { updateProfile } from "../../remote/Profile";

interface Props {
  id: number;
}

interface AuthenticatedProps extends Props {
  context: ProfileContextAuthenticatedInterface;
}

const FavoriteProductUnauthenticated: React.FC<unknown> = () => {
  const [show, toggle] = useToggle(false);
  const setFalse = useCallback(() => toggle(false), [toggle]);
  const setTrue = useCallback(() => toggle(true), [toggle]);

  return (
    <>
      {show && (
        <ModalPopup dismiss={setFalse}>
          <div className="box content has-text-centered">
            <h2>Login</h2>
            <p>Login to save save and remember this product.</p>
            <LoginButton />
          </div>
        </ModalPopup>
      )}
      <button
        type="button"
        onClick={setTrue}
        className="button is-ghost"
        style={{ borderWidth: 0, outline: 0, boxShadow: "none" }}
      >
        <i
          className={cn({
            "has-text-primary far fa-bookmark": true,
          })}
        />
      </button>
    </>
  );
};

const FavoriteProductAuthenticated: React.FC<AuthenticatedProps> = ({ id, context }) => {
  const [isPending, toggle] = useToggle(false);
  const [exists, savedProducts] = useMemo(() => {
    if (context.status === "authenticated") {
      const saved = (context.data?.savedProducts ?? []).indexOf(id) === -1;
      let updated;

      if (saved) {
        updated = (context.data?.savedProducts ?? []).concat([id]);
      } else {
        updated = (context.data?.savedProducts ?? []).filter((productId) => productId !== id);
      }

      return [saved, updated];
    } else {
      return [false, []];
    }
  }, [id, context]);

  const auth0 = useAuth0AccessTokenContext();

  return (
    <button
      type="button"
      onClick={async () => {
        if (auth0.status === "authenticated") {
          toggle(true);
          try {
            await updateProfile(
              {
                ...context.data,
                savedProducts,
              },
              auth0.token
            );
          } finally {
            toggle(false);
          }
        }
      }}
      className={cn({ "button is-ghost": true, "is-loading": isPending })}
      style={{ borderWidth: 0, outline: 0, boxShadow: "none" }}
    >
      <i
        className={cn({
          "has-text-primary fa-bookmark": true,
          far: exists,
          fas: !exists,
          "is-hidden": status === "pending",
        })}
      />
    </button>
  );
};

export default function FavoriteProduct(props: Props) {
  const { id } = props;
  const context = useProfileContext();

  if (context.status === "authenticated") {
    return <FavoriteProductAuthenticated id={id} context={context} />;
  } else {
    return <FavoriteProductUnauthenticated />;
  }
}
